import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'phosphor-react'
import { Button } from 'semantic-ui-react'
import styled from 'styled-components'
import { darkControls, surface, textColor } from './ui'

const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

// Open overlays, innermost last: only the top one reacts to Escape and Tab.
const overlayStack = []

/**
 * Keeps Tab inside `ref` while `open`, closes on Escape, focuses the first control on open
 * and returns focus to whatever had it before.
 */
const useFocusTrap = (ref, open, onClose) => {
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        if (!open) return undefined
        const token = {}
        overlayStack.push(token)
        const previous = document.activeElement
        const node = ref.current
        const focusables = () => (node ? Array.from(node.querySelectorAll(FOCUSABLE)) : [])
        const first = focusables()[0]
        if (first) first.focus()
        else if (node) node.focus()

        const onKeyDown = event => {
            if (overlayStack[overlayStack.length - 1] !== token) return
            if (event.key === 'Escape') {
                event.stopPropagation()
                onCloseRef.current()
                return
            }
            if (event.key !== 'Tab') return
            const items = focusables()
            if (!items.length) {
                event.preventDefault()
                return
            }
            const firstItem = items[0]
            const lastItem = items[items.length - 1]
            if (event.shiftKey && document.activeElement === firstItem) {
                event.preventDefault()
                lastItem.focus()
            } else if (!event.shiftKey && document.activeElement === lastItem) {
                event.preventDefault()
                firstItem.focus()
            }
        }
        document.addEventListener('keydown', onKeyDown, true)
        return () => {
            document.removeEventListener('keydown', onKeyDown, true)
            const at = overlayStack.indexOf(token)
            if (at >= 0) overlayStack.splice(at, 1)
            if (previous && typeof previous.focus === 'function' && document.contains(previous)) previous.focus()
        }
    }, [open, ref])
}

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1500;
    display: flex;
    justify-content: ${({ $side }) => ($side ? 'flex-end' : 'center')};
    align-items: ${({ $side }) => ($side ? 'stretch' : 'center')};
`

const Sheet = styled.div`
    background: ${surface};
    color: ${textColor};
    display: flex;
    flex-direction: column;
    outline: none;
    ${darkControls}
    ${({ $side, $width }) =>
        $side
            ? `width: min(${$width}px, 100vw); height: 100%; box-shadow: -8px 0 24px rgba(0,0,0,.2);
               @media (max-width: 768px) { width: 100vw; }`
            : `width: min(${$width}px, calc(100vw - 24px)); max-height: calc(100vh - 32px);
               border-radius: 10px; box-shadow: 0 12px 40px rgba(0,0,0,.3);
               @media (max-width: 768px) { width: 100vw; max-height: 100vh; height: 100%; border-radius: 0; }`}
`

const SheetHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    h3 {
        margin: 0;
        font-size: 1.1rem;
    }
    p {
        margin: 2px 0 0;
        opacity: 0.7;
        font-size: 0.85rem;
    }
`

const SheetBody = styled.div`
    padding: 16px 20px;
    overflow-y: auto;
    flex: 1;
`

const SheetFooter = styled.div`
    padding: 12px 20px;
    border-top: 1px solid rgba(128, 128, 128, 0.25);
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
`

const CloseButton = styled.button`
    background: none;
    border: 0;
    cursor: pointer;
    color: inherit;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    &:hover {
        background: rgba(128, 128, 128, 0.2);
    }
`

const Overlay = ({ open, onClose, title, subtitle, children, footer, width, side, printable }) => {
    const ref = useRef(null)
    useFocusTrap(ref, open, onClose)
    if (!open) return null
    return createPortal(
        <Backdrop
            $side={side}
            data-crm-print={printable ? 'true' : undefined}
            onMouseDown={event => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <Sheet ref={ref} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} $side={side} $width={width}>
                <SheetHeader>
                    <div>
                        <h3>{title}</h3>
                        {subtitle ? <p>{subtitle}</p> : null}
                    </div>
                    <CloseButton type="button" aria-label="Close" onClick={onClose} className="crm-no-print">
                        <X size={20} />
                    </CloseButton>
                </SheetHeader>
                <SheetBody>{children}</SheetBody>
                {footer ? <SheetFooter className="crm-no-print">{footer}</SheetFooter> : null}
            </Sheet>
        </Backdrop>,
        document.body,
    )
}

/** Right-hand drawer; full screen on phones. */
export const CrmDrawer = ({ width = 560, ...props }) => <Overlay {...props} width={width} side />

/** Centered dialog; full screen on phones. */
export const CrmModal = ({ width = 560, ...props }) => <Overlay {...props} width={width} side={false} />

/** Yes/No confirmation, e.g. before a delete. */
export const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', danger, loading, onConfirm, onClose }) => (
    <CrmModal
        open={open}
        onClose={onClose}
        title={title}
        width={440}
        footer={
            <>
                <Button type="button" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button type="button" negative={danger} primary={!danger} loading={loading} disabled={loading} onClick={onConfirm}>
                    {confirmLabel}
                </Button>
            </>
        }
    >
        <p>{message}</p>
    </CrmModal>
)
